import moment from 'moment';
import { parseISODateString } from '@homeexchange/shared';

/**
 * Compare two search objects to determine if they represent the same search
 * @param {Object} alertData - The saved alert data
 * @param {Object} currentSearch - The current search object
 * @returns {boolean} - True if searches match
 */
export function compareSearchCriteria(alertData, currentSearch) {
    const alertCriteria = alertData.searchCriteria || {};
    const currentFilters = currentSearch.filters || {};

    if (!compareLocationDetails(alertCriteria.location, currentFilters.location)) {
        return false;
    }

    if (!compareDateRanges(alertCriteria.calendar?.dateRanges, currentFilters.calendar?.date_ranges)) {
        return false;
    }

    if (!compareGuestCriteria(alertCriteria.guestCriteria?.guests, currentFilters.home?.size?.beds)) {
        return false;
    }

    return true;
}

/**
 * Compare date ranges between alert and current search
 * @param {Array} alertDateRanges - Date ranges from saved alert
 * @param {Array} currentDateRanges - Date ranges from current search
 * @returns {boolean} - True if date ranges match
 */
function compareDateRanges(alertDateRanges = [], currentDateRanges = []) {
    if (alertDateRanges.length !== currentDateRanges.length) {
        return false;
    }

    for (let i = 0; i < alertDateRanges.length; i++) {
        const alertFrom = moment(parseISODateString(alertDateRanges[i].from));
        const alertTo = moment(parseISODateString(alertDateRanges[i].to));
        const currentFrom = moment(parseISODateString(currentDateRanges[i].from));
        const currentTo = moment(parseISODateString(currentDateRanges[i].to));

        if (!alertFrom.isSame(currentFrom, 'day') || !alertTo.isSame(currentTo, 'day')) {
            return false;
        }
    }

    return true;
}

/**
 * Compare guest criteria between alert and current search
 * @param {Object} alertGuests - Guest criteria from saved alert
 * @param {Object} currentGuests - Guest criteria from current search
 * @returns {boolean} - True if guest criteria match
 */
function compareGuestCriteria(alertGuests = {}, currentGuests = {}) {
    const alertAdults = alertGuests.adults || 0;
    const alertChildren = alertGuests.children || 0;
    const alertBabies = alertGuests.babies || 0;

    const currentAdults = currentGuests.adults || 0;
    const currentChildren = currentGuests.children || 0;
    const currentBabies = currentGuests.babies || 0;

    return (
        alertAdults === currentAdults && alertChildren === currentChildren && alertBabies === currentBabies
    );
}

export const BOUNDS_TOLERANCE = 1e-4;

export function boundsMatch(a, b) {
    return ['sw', 'ne'].every((corner) =>
        ['lat', 'lon'].every(
            (axis) => Math.abs(Number(a?.[corner]?.[axis]) - Number(b?.[corner]?.[axis])) <= BOUNDS_TOLERANCE
        )
    );
}

/**
 * Compare location details between alert and current search
 * @param {Object} alertLocation - Location details from saved alert
 * @param {Object} currentLocation - `filters.location` of the current search (polygon and/or bounds)
 * @returns {boolean} - True if location details match
 */
function compareLocationDetails(alertLocation = {}, currentLocation = {}) {
    const currentLocationId = currentLocation.polygon?.location_id;

    if (alertLocation.locationId && currentLocationId) {
        return alertLocation.locationId === currentLocationId;
    }

    // A map area only ever matches another map area, never a named place.
    const alertBounds = alertLocation.locationId ? null : alertLocation.bounds;
    const currentBounds = currentLocationId ? null : currentLocation.bounds;
    if (alertBounds || currentBounds) {
        return Boolean(alertBounds && currentBounds) && boundsMatch(alertBounds, currentBounds);
    }

    return true;
}

/**
 * Find existing alert that matches the current search
 * @param {Array} alerts - Array of saved alerts
 * @param {Object} currentSearch - Current search object with query and filters
 * @returns {Object|null} - Matching alert or null if not found
 */
export function findExistingAlert(alerts, currentSearch) {
    const hasQuery = Boolean(currentSearch?.query);
    const hasBounds = Boolean(currentSearch?.filters?.location?.bounds);
    if (!alerts || alerts.length === 0 || (!hasQuery && !hasBounds)) {
        return null;
    }

    return (
        alerts.find((alert) => {
            const alertData = alert.attributes || alert;
            return compareSearchCriteria(alertData, currentSearch);
        }) || null
    );
}
